import * as React from 'react';

/**
 * Tracks whether a CSS media query currently matches, updating live as the
 * viewport crosses it.
 *
 * Prefer this over a `resize` listener + manual width comparison when the
 * only thing that matters is a single true/false threshold (e.g. "are we
 * past the tablet breakpoint") — `matchMedia` only notifies on an actual
 * change of the match, rather than firing on every pixel of a resize.
 *
 * `window.matchMedia` is unimplemented in jsdom, so this resolves to `false`
 * there unless a test stubs it — see `use-media-query.test.ts`.
 *
 * @param query - A CSS media query string, e.g. `'(min-width: 821px)'`.
 * @param options.isEnabled - When false the hook is inert: it resolves to
 * `false` and registers no `matchMedia` listener. For callers that must invoke
 * the hook unconditionally (Rules of Hooks) but only sometimes care about the
 * query.
 * @returns Whether the query currently matches.
 *
 * @example
 * const isSideBySideTier = useMediaQuery('(min-width: 821px)', { isEnabled: isResizable });
 */
function useMediaQuery(query: string, options?: { isEnabled?: boolean }): boolean {
  const isEnabled = options?.isEnabled ?? true;

  const getSnapshot = React.useCallback(() => {
    if (!isEnabled) return false;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  }, [query, isEnabled]);

  const [matches, setMatches] = React.useState(getSnapshot);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    if (!isEnabled) {
      // A hook disabled after matching must not keep reporting the stale match.
      setMatches(false);
      return;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = () => setMatches(mediaQueryList.matches);

    // The query itself (not just the listener) may have changed since the
    // initial render's snapshot.
    handleChange();

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    // Safari < 14 only supports the deprecated listener API.
    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [query, isEnabled]);

  return matches;
}

export { useMediaQuery };
